import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const R = 22; // ball radius
const GRAVITY = 0.5;
const TARGET = 3;

const geoFrom = ({ w, h }) => {
  const binW = 92;
  const binH = 104;
  const binX = w / 2 - binW / 2;
  const binTop = Math.max(56, h * 0.3);
  return { w, h, binW, binH, binX, binTop, floor: h - 12 };
};

const PaperTossGame = () => {
  const areaRef = useRef(null);
  const ballRef = useRef(null);
  const rafRef = useRef(0);
  const pos = useRef({ x: 0, y: 0 });
  const vel = useRef({ x: 0, y: 0 });
  const drag = useRef(null);
  const flying = useRef(false);
  const sizeRef = useRef({ w: 0, h: 0 });

  const [score, setScore] = useState(0);
  const [status, setStatus] = useState("idle"); // idle | flying | scored | missed | win
  const [inBin, setInBin] = useState(false);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  const startPos = useCallback(() => {
    const { w, h } = sizeRef.current;
    return { x: w / 2, y: h - R - 18 };
  }, []);

  // Direct DOM write — no React re-render per frame (smooth).
  // Paper shrinks as it travels toward the bin (further away) for depth.
  const applyBall = useCallback(() => {
    const el = ballRef.current;
    if (!el) return;
    const g = geoFrom(sizeRef.current);
    const startY = sizeRef.current.h - R - 18;
    const range = startY - g.binTop || 1;
    const p = Math.min(1, Math.max(0, (startY - pos.current.y) / range));
    const s = 1 - p * 0.55;
    el.style.transform = `translate3d(${pos.current.x - R}px, ${pos.current.y - R}px, 0) scale(${s})`;
  }, []);

  const resetBall = useCallback(() => {
    const sp = startPos();
    pos.current = { ...sp };
    vel.current = { x: 0, y: 0 };
    flying.current = false;
    setInBin(false);
    setStatus("idle");
    applyBall();
  }, [startPos, applyBall]);

  useEffect(() => {
    const measure = () => {
      const el = areaRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      sizeRef.current = { w: r.width, h: r.height };
      setDims({ w: r.width, h: r.height });
      if (!flying.current) resetBall();
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [resetBall]);

  const step = useCallback(() => {
    const g = geoFrom(sizeRef.current);
    const prevX = pos.current.x;
    const prevY = pos.current.y;
    vel.current.y += GRAVITY;
    pos.current.x += vel.current.x;
    pos.current.y += vel.current.y;

    // interpolate the crossing of the bin mouth plane (prevents tunneling)
    const py0 = prevY + R;
    const py1 = pos.current.y + R;
    let enteringMouth = false;
    if (vel.current.y > 0 && py0 <= g.binTop && py1 >= g.binTop) {
      const t = (g.binTop - py0) / (py1 - py0 || 1);
      const xAt = prevX + t * (pos.current.x - prevX);
      enteringMouth = xAt > g.binX + 10 && xAt < g.binX + g.binW - 10;
    }

    if (enteringMouth) {
      flying.current = false;
      cancelAnimationFrame(rafRef.current);
      pos.current.x = g.binX + g.binW / 2;
      pos.current.y = g.binTop + 34;
      applyBall();
      setInBin(true);
      setScore((s) => {
        const ns = s + 1;
        if (ns >= TARGET) {
          setStatus("win");
          setTimeout(() => {
            setScore(0);
            resetBall();
          }, 1700);
        } else {
          setStatus("scored");
          setTimeout(resetBall, 700);
        }
        return ns;
      });
      return;
    }

    const out =
      pos.current.y - R > g.floor ||
      pos.current.x + R < 0 ||
      pos.current.x - R > g.w;
    if (out) {
      flying.current = false;
      cancelAnimationFrame(rafRef.current);
      setStatus("missed");
      setTimeout(resetBall, 600);
      return;
    }

    applyBall();
    rafRef.current = requestAnimationFrame(step);
  }, [resetBall, applyBall]);

  const onPointerDown = (e) => {
    if (flying.current || status === "win") return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const r = areaRef.current.getBoundingClientRect();
    const px = e.clientX - r.left;
    const py = e.clientY - r.top;
    drag.current = {
      offX: pos.current.x - px,
      offY: pos.current.y - py,
      sx: pos.current.x,
      sy: pos.current.y,
      lastX: px,
      lastY: py,
      lastT: performance.now(),
      vx: 0,
      vy: 0,
    };
  };

  const onPointerMove = (e) => {
    if (!drag.current) return;
    const r = areaRef.current.getBoundingClientRect();
    const px = e.clientX - r.left;
    const py = e.clientY - r.top;
    const now = performance.now();
    const dt = Math.max(1, now - drag.current.lastT);
    drag.current.vx = (px - drag.current.lastX) / dt;
    drag.current.vy = (py - drag.current.lastY) / dt;
    drag.current.lastX = px;
    drag.current.lastY = py;
    drag.current.lastT = now;
    pos.current.x = px + drag.current.offX;
    pos.current.y = py + drag.current.offY;
    applyBall();
  };

  const onPointerUp = () => {
    if (!drag.current) return;
    const factor = 9;
    const cap = 28;
    let vx = drag.current.vx * factor;
    let vy = drag.current.vy * factor;
    const speed = Math.hypot(vx, vy);

    if (speed < 6) {
      const dx = pos.current.x - drag.current.sx;
      const dy = pos.current.y - drag.current.sy;
      const d = Math.hypot(dx, dy);
      if (d < 24) {
        drag.current = null;
        resetBall();
        return;
      }
      const power = Math.min(26, d * 0.45);
      const a = Math.atan2(dy, dx);
      vx = Math.cos(a) * power;
      vy = Math.sin(a) * power;
    }

    vx = Math.max(-cap, Math.min(cap, vx));
    vy = Math.max(-cap, Math.min(cap, vy));
    drag.current = null;
    vel.current = { x: vx, y: vy };
    flying.current = true;
    setStatus("flying");
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(step);
  };

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const g = geoFrom(dims);

  return (
    <section
      data-testid="game-section"
      className="relative overflow-hidden border-y border-black/15 bg-[#f7f3ea] py-10 md:py-14"
    >
      <div className="px-6 md:px-12 lg:px-16 flex items-baseline justify-between mb-4">
        <span className="label text-[#0a0a0a]/40">(Take a break — toss the paper)</span>
        <span
          data-testid="game-score"
          className="tight font-extrabold text-2xl md:text-3xl tabular-nums text-[#0a0a0a]"
        >
          {score}
          <span className="text-[#0a0a0a]/30">/{TARGET}</span>
        </span>
      </div>

      <div
        ref={areaRef}
        data-testid="game-area"
        data-lenis-prevent
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerUp}
        className="relative mx-4 md:mx-8 h-[320px] md:h-[380px] rounded-xl bg-[#efe9dc] overflow-hidden select-none touch-none"
      >
        <span className="pointer-events-none absolute left-6 top-5 label text-[#0a0a0a]/30">
          flick the paper up into the bin ↑
        </span>

        {/* bin */}
        <div
          data-testid="game-bin"
          className="absolute"
          style={{ left: g.binX, top: g.binTop, width: g.binW, height: g.binH }}
        >
          <div className="absolute -top-1 left-0 right-0 h-3 rounded-[50%] border-2 border-[#0a0a0a]/70 bg-[#e4ddcd]" />
          <div
            className="absolute top-1 left-1 right-1 bottom-0 bg-[#0a0a0a]/85"
            style={{
              clipPath: "polygon(6% 0, 94% 0, 86% 100%, 14% 100%)",
              backgroundImage:
                "repeating-linear-gradient(90deg, rgba(255,255,255,0.14) 0 2px, transparent 2px 9px), repeating-linear-gradient(0deg, rgba(255,255,255,0.10) 0 2px, transparent 2px 12px)",
            }}
          />
          <div className="absolute -top-1 left-0 right-0 h-3 rounded-[50%] border-2 border-[#0047FF]" />
        </div>

        {/* paper ball (positioned via transform, no per-frame React render) */}
        <div
          ref={ballRef}
          data-testid="game-ball"
          onPointerDown={onPointerDown}
          data-cursor-hover
          className="absolute left-0 top-0 z-20 cursor-grab active:cursor-grabbing touch-none"
          style={{ width: R * 2, height: R * 2, touchAction: "none", willChange: "transform" }}
        >
          <motion.div
            animate={inBin ? { scale: 0.5, opacity: 0 } : { scale: 1, opacity: 1 }}
            transition={{ duration: inBin ? 0.4 : 0.15 }}
            className="pointer-events-none h-full w-full drop-shadow-[0_6px_14px_rgba(0,0,0,0.22)]"
          >
            <svg viewBox="0 0 48 48" className="h-full w-full">
              <path
                d="M24 2 L32 6 L40 5 L44 13 L46 23 L42 31 L45 40 L36 45 L26 43 L17 46 L9 41 L3 32 L6 22 L2 13 L11 7 Z"
                fill="#f7f4ec"
                stroke="#0a0a0a"
                strokeOpacity="0.28"
                strokeWidth="1"
                strokeLinejoin="round"
              />
              <path d="M24 2 L32 6 L26 21 L11 7 Z" fill="#0a0a0a" opacity="0.05" />
              <path d="M46 23 L42 31 L26 21 L44 13 Z" fill="#0a0a0a" opacity="0.07" />
              <path d="M45 40 L36 45 L26 21 L42 31 Z" fill="#0a0a0a" opacity="0.04" />
              <path d="M9 41 L3 32 L26 21 L17 46 Z" fill="#0a0a0a" opacity="0.06" />
              <g stroke="#0a0a0a" strokeOpacity="0.2" strokeWidth="0.8" fill="none" strokeLinecap="round">
                <path d="M26 21 L24 2" />
                <path d="M26 21 L46 23" />
                <path d="M26 21 L36 45" />
                <path d="M26 21 L3 32" />
                <path d="M26 21 L11 7" />
                <path d="M26 21 L44 13" />
              </g>
            </svg>
          </motion.div>
        </div>

        {/* feedback */}
        <AnimatePresence>
          {(status === "scored" || status === "missed" || status === "win") && (
            <motion.div
              key={status + score}
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="pointer-events-none absolute inset-x-0 top-6 flex justify-center"
            >
              <span
                className={`tight font-extrabold text-3xl md:text-5xl ${
                  status === "missed" ? "text-[#0a0a0a]/40" : "text-[#0047FF]"
                }`}
              >
                {status === "win" ? "NICE. 3/3 ✦" : status === "scored" ? "+1" : "miss"}
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default PaperTossGame;
